<template>
  <app-page icon="grid" title="Dashboard" lean>
    <template v-if="!handy" v-slot:actions>
      <!-- mit Beschriftung wie die Aktionen auf Verkäufe und Exporte (Kundenwunsch 13.10.2026) -->
      <app-button icon="refresh" title="Kennzahlen aktualisieren" @click="refreshAll">Aktualisieren</app-button>
      <app-month-stepper v-model="month" :max="currentMonth" />
    </template>

    <div class="ov app-page-pad" :class="{ 'ov--phone': handy }">
      <div v-if="handy" class="ov__stepper">
        <app-month-stepper v-model="month" :max="currentMonth" grow />
      </div>

      <p v-if="!hasContent" class="ov__none">Für Ihr Konto sind keine Kennzahlen freigeschaltet.</p>

      <!-- Kennzahlen: Einnahmen, Ticketverkäufe, Saldo {Jahr}, Busse unterwegs, Aktive Benutzer, Aktive Geräte -->
      <section v-if="hasTiles" class="ov__kpis" aria-label="Kennzahlen">
        <app-kpi-tile
          v-if="can.sum"
          :to="salesTileLink"
          icon="wallet"
          title="Einnahmen"
          :state="tileState('overview')"
          :graphic-height="barsHeight"
          @retry="retry('overview')"
        >
          <template v-slot:value>
            <span :class="valueClass(overview.sum)">{{ money(overview.sum) }}</span><span
              v-if="isNumber(overview.sum)"
              class="app-eur"
            >€</span>
          </template>
          <template v-slot:graphic>
            <app-mini-bars v-if="saldoRows" :values="revenueAverages" :current="monthIndex" :height="barsHeight" />
            <span v-else-if="saldoPending" class="app-kpi__track" aria-hidden="true"></span>
          </template>
          <template v-slot:foot>
            <template v-if="saldoRows">
              <span><app-icon v-if="revenueTrend.icon" :name="revenueTrend.icon" :size="13" />{{ revenueTrend.text }}</span>
              <span v-if="!handy">{{ rangeLabel }}</span>
            </template>
            <span v-else-if="saldoForbidden" class="app-kpi__muted">Keine Berechtigung</span>
            <button
              v-else-if="saldoFailed"
              type="button"
              class="app-link app-link--inline"
              @click.prevent.stop="retry('saldo')"
            >Erneut versuchen</button>
          </template>
        </app-kpi-tile>

        <app-kpi-tile
          v-if="can.count"
          :to="salesTileLink"
          icon="ticket"
          title="Ticketverkäufe"
          :state="tileState('overview')"
          :graphic-height="barsHeight"
          @retry="retry('overview')"
        >
          <template v-slot:value>
            <span :class="valueClass(overview.count)">{{ count(overview.count) }}</span>
          </template>
          <template v-slot:graphic>
            <app-mini-bars v-if="saldoRows" :values="ticketAverages" :current="monthIndex" :height="barsHeight" />
            <span v-else-if="saldoPending" class="app-kpi__track" aria-hidden="true"></span>
          </template>
          <template v-slot:foot>
            <template v-if="saldoRows">
              <span><app-icon v-if="ticketTrend.icon" :name="ticketTrend.icon" :size="13" />{{ ticketTrend.text }}</span>
              <span v-if="!handy">{{ rangeLabel }}</span>
            </template>
            <span v-else-if="saldoForbidden" class="app-kpi__muted">Keine Berechtigung</span>
            <button
              v-else-if="saldoFailed"
              type="button"
              class="app-link app-link--inline"
              @click.prevent.stop="retry('saldo')"
            >Erneut versuchen</button>
          </template>
        </app-kpi-tile>

        <app-kpi-tile
          v-if="can.sum && !handy"
          :to="links.saldo"
          icon="scale"
          :title="'Saldo ' + ym.year"
          :state="tileState('saldo')"
          :graphic-height="barsHeight"
          @retry="retry('saldo')"
        >
          <template v-slot:value>
            {{ money(saldo.total) }}<span class="app-eur">€</span>
          </template>
          <template v-slot:graphic>
            <app-mini-bars :values="saldo.bars" :current="monthIndex" :height="barsHeight" />
          </template>
          <template v-slot:foot>
            <span v-if="saldo.open.length">{{ count(saldo.open.length) }} Fahrer offen</span>
            <span v-else>alle ausgeglichen</span>
            <span>{{ rangeLabel }}</span>
          </template>
        </app-kpi-tile>

        <app-kpi-tile
          v-if="can.live && !handy"
          :to="links.live"
          icon="bus"
          title="Busse unterwegs"
          :state="tileState('gps')"
          @retry="retry('gps')"
        >
          <template v-slot:value>{{ count(buses.n) }}</template>
          <template v-slot:graphic>
            <app-share-bar :parts="busParts" :total="buses.n" :gap="2" />
          </template>
          <template v-slot:foot>
            <span v-if="!buses.n">gerade kein Bus unterwegs</span>
            <!-- Legende der Ampel wie unter der Live-Karte -->
            <span v-else class="app-legend">
              <span v-for="item in delayLegend" :key="item.key" class="app-legend__item"><span
                class="app-legend__dot"
                :style="{ '--dot': item.dot }"
              ></span>{{ item.text }}</span>
            </span>
          </template>
        </app-kpi-tile>

        <app-kpi-tile
          v-if="can.users && !handy"
          :to="links.users"
          icon="userCheck"
          title="Aktive Benutzer"
          :state="tileState('user')"
          @retry="retry('user')"
        >
          <template v-slot:value>{{ count(users.active) }}</template>
          <template v-slot:graphic>
            <app-share-bar :parts="activeParts(users)" :total="users.total" />
          </template>
          <template v-slot:foot>
            <span>{{ count(users.inactive) }} inaktiv</span>
            <span>von {{ count(users.total) }} {{ plural(users.total, 'Benutzer', 'Benutzern') }}</span>
          </template>
        </app-kpi-tile>

        <app-kpi-tile
          v-if="can.devices && !handy"
          :to="links.devices"
          icon="tablet"
          title="Aktive Geräte"
          :state="tileState('device')"
          @retry="retry('device')"
        >
          <template v-slot:value>{{ count(devices.active) }}</template>
          <template v-slot:graphic>
            <app-share-bar :parts="activeParts(devices)" :total="devices.total" />
          </template>
          <template v-slot:foot>
            <span>{{ count(devices.inactive) }} inaktiv</span>
            <span>von {{ count(devices.total) }} {{ plural(devices.total, 'Gerät', 'Geräten') }}</span>
          </template>
        </app-kpi-tile>
      </section>

      <!-- Reihe 2: Einnahmen je Tag und Zahlungsarten -->
      <div v-if="can.sum" class="ov__row">
        <app-panel
          nested
          class="ov__card ov__card--wide"
          icon="barChart"
          skeleton="bars"
          title="Einnahmen"
          :subtitle="revenueSubtitle"
          :link-to="links.sales"
          link-text="Verkäufe"
          :card-link="phoneCardLink(revenueState)"
          :state="revenueState"
          @retry="retry('driversettlement')"
        >
          <ov-revenue-chart
            v-if="revenueDays"
            :days="revenueDays"
            :height="chart.height"
            :gap="chart.gap"
            :axis-width="chart.axisWidth"
            :label-every="chart.labelEvery"
            :today-key="today"
            :tooltips="!handy"
          />
          <div v-else :style="spacer(chart.height + 24)"></div>
        </app-panel>

        <app-panel
          nested
          class="ov__card ov__card--narrow ov__card--column"
          icon="pie"
          skeleton="donut"
          title="Zahlungsarten"
          :subtitle="monthLabel"
          :link-to="links.sales"
          link-text="Verkäufe"
          :card-link="phoneCardLink(splitState)"
          :state="splitState"
          @retry="retry('saldo')"
        >
          <ov-payment-split
            v-if="split"
            :cash="split.cash"
            :card="split.card"
            :voucher="split.voucher"
            :month-label="monthLabel"
            :size="donut.size"
            :ring="donut.ring"
            :row-height="donut.rowHeight"
            :side="handy"
          />
          <div v-else :style="spacer(handy ? donut.size : donut.size + 16 + 2 * donut.rowHeight + 1)"></div>
        </app-panel>
      </div>

      <!-- Reihe 3: Live-Karte und Offene Beträge -->
      <div v-if="can.live || can.sum" class="ov__row">
        <app-panel
          nested
          v-if="can.live"
          class="ov__card ov__card--wide ov__card--map"
          icon="pin"
          flush
          skeleton="block"
          title="Live-Karte"
          :link-to="links.live"
          link-text="Live-Tracking"
          :card-link="phoneCardLink(mapState)"
          :state="mapState"
          :state-title="mapState === 'empty' ? 'Gerade ist kein Bus unterwegs.' : null"
          :state-icon="mapState === 'empty' ? 'bus' : null"
          @retry="retry('gps')"
        >
          <template v-if="handy && gpsRows" v-slot:subtitle>
            {{ busCountText }}<template v-if="buses.late"> · <span class="ov__late"><span class="app-legend__dot" style="--dot: var(--st-late)"></span>{{ count(buses.late) }} &gt; 10 Min.</span></template>
          </template>
          <ov-live-map
            v-if="gpsRows && buses.n"
            :buses="gpsRows"
            :height="mapHeight"
            :compact="handy"
          />
          <div v-else :style="spacer(mapHeight)"></div>
          <!-- Legende der Ampelpunkte auf dem Rahmen unter der Karte -->
          <template v-if="gpsRows && buses.n" v-slot:foot>
            <span class="app-legend">
              <span v-for="item in delayLegend" :key="item.key" class="app-legend__item"><span
                class="app-legend__dot"
                :style="{ '--dot': item.dot }"
              ></span>{{ item.text }}</span>
            </span>
          </template>
        </app-panel>

        <app-panel
          nested
          v-if="can.sum"
          class="ov__card ov__card--narrow"
          icon="scale"
          skeleton="rows"
          :skeleton-rows="handy ? 3 : 4"
          title="Offene Beträge"
          :link-to="links.saldo"
          link-text="Saldo"
          :card-link="phoneCardLink(openState)"
          :state="openState"
          flush
          @retry="retry('saldo')"
        >
          <template v-if="handy && open" v-slot:subtitle>
            <template v-if="open.openCount">{{ count(open.openCount) }} offen · {{ euro(open.openSum) }}</template>
            <template v-else>alle ausgeglichen</template>
          </template>
          <ov-open-amounts v-if="open" :rows="open.rows" :compact="handy" />
          <div v-else :style="spacer(handy ? 4 * 48 + 8 : 4 * 64 + 8)"></div>
          <!-- „weitere …“ auf dem Rahmen unter der Karte wie der Fuß der Kennzahl-Kacheln -->
          <template v-if="openFoot" v-slot:foot>
            <span><app-icon v-if="openFoot.icon" :name="openFoot.icon" :size="13" />{{ openFoot.left }}</span>
            <span>{{ openFoot.right }}</span>
          </template>
        </app-panel>
      </div>
    </div>
  </app-page>
</template>

<script>
// Übersicht (Spezifikation 4.1): Kennzahl-Kacheln und vier Karten. Jede Datenquelle lädt einzeln mit eigenem Lade- und
// Fehlerzustand (kein Promise.all); nur die Antwort des letzten Aufrufs je Quelle zählt. Nur GETs an bestehende
// Endpunkte; Rechteschlüssel wie bisher (auch der Tippfehler VIEW_OVERIVEW_SALE_SUM_TILE). Berechnungen in
// src/components/overview/overviewData.js. Handy: kein Seitenkopf, Aktualisieren in der App-Leiste, zwei Kacheln.
import axios from 'axios';
import { loadSaldoMonths } from '@/utils/saldoMonths';
import moment from 'moment';
import { hasPermission } from '@/auth/auth';
import { isPathAllowed } from '@/shell/navigation';
import { setPhoneAction } from '@/shell/shellState';
import {
  formatEuro,
  formatMonth,
  formatMonthShort,
  formatNumber,
  formatPercent,
  LEER,
  MINUS,
  plural
} from '@/utils/format';
import {
  activeSummary,
  averageTrend,
  busSummary,
  monthlyAverages,
  openAmounts,
  overviewValues,
  paymentSplit,
  revenueDays,
  revenueRange,
  saldoSummary,
  saldoToMonth,
  splitMonth,
  todayInfo,
  DELAY_LEGEND
} from '@/components/overview/overviewData';
import OvRevenueChart from '@/components/overview/OvRevenueChart.vue';
import OvPaymentSplit from '@/components/overview/OvPaymentSplit.vue';
import OvOpenAmounts from '@/components/overview/OvOpenAmounts.vue';
import OvLiveMap from '@/components/overview/OvLiveMap.vue';

const SOURCES = ['overview', 'saldo', 'driversettlement', 'gps', 'user', 'device'];

// Zustand einer Datenquelle: s = 'idle' | 'loading' | 'ready' | 'error', status = HTTP-Status, data = eingefrorene Liste
function idle() {
  return { s: 'idle', status: null, data: null };
}

function freezeList(data) {
  return Object.freeze(Array.isArray(data) ? data : []);
}

function statusOf(error) {
  return error && error.response ? error.response.status : null;
}

export default {
  name: 'HomePage',
  components: { OvRevenueChart, OvPaymentSplit, OvOpenAmounts, OvLiveMap },
  data() {
    const now = moment();
    return {
      month: now.format('YYYY-MM'),
      today: now.format('YYYY-MM-DD'),
      todayUtc: moment.utc().format('YYYY-MM-DD'),
      src: {
        overview: idle(),
        saldo: idle(),
        driversettlement: idle(),
        gps: idle(),
        user: idle(),
        device: idle()
      }
    };
  },
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    // Tablet hoch (Touch, schmaler als 960): Maße aus dem Tablet-Entwurf
    tablet() {
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : 1280;
      return !this.handy && this.touch && width < 960;
    },
    can() {
      return {
        sum: hasPermission('VIEW_OVERIVEW_SALE_SUM_TILE'),
        count: hasPermission('VIEW_OVERVIEW_SALE_COUNT_TILE'),
        live: hasPermission('VIEW_OVERVIEW_LIVETRACKING_TILE'),
        users: hasPermission('VIEW_OVERVIEW_USER_TILE'),
        devices: hasPermission('VIEW_OVERVIEW_DEVICES_TILE')
      };
    },
    // welche Quellen die sichtbaren Kacheln und Karten brauchen (Spezifikation 4.1.3)
    needs() {
      const c = this.can;
      return {
        overview: c.sum || c.count,
        saldo: c.sum || c.count,
        driversettlement: c.sum,
        gps: c.live,
        user: c.sum || (c.users && !this.handy),
        device: c.devices && !this.handy
      };
    },
    // primitiver Schlüssel für den Lade-Watcher
    needsKey() {
      return SOURCES.filter(key => this.needs[key]).join(',');
    },
    hasTiles() {
      const c = this.can;
      return c.sum || c.count || (!this.handy && (c.live || c.users || c.devices));
    },
    hasContent() {
      return this.hasTiles || this.can.sum || this.can.live;
    },
    // Kachel- und Kartenlinks nur bei Menürecht (sonst reine Anzeige ohne › und Hover)
    links() {
      const allowed = path => (isPathAllowed(path, this.$router) ? path : null);
      return {
        sales: allowed('/administration/ticketsales'),
        saldo: allowed('/administration/saldo'),
        live: allowed('/analytics/live'),
        users: allowed('/masterdata/users'),
        devices: allowed('/masterdata/devices')
      };
    },
    currentMonth() {
      return this.today.slice(0, 7);
    },
    ym() {
      return splitMonth(this.month) || splitMonth(this.currentMonth);
    },
    monthIndex() {
      return this.ym.month - 1;
    },
    todayInfo() {
      return todayInfo(this.today, this.todayUtc);
    },
    monthLabel() {
      return formatMonth(this.month);
    },
    // „Jan.–Okt.“
    rangeLabel() {
      const m = this.ym.month;
      return m === 1 ? formatMonthShort(1) : formatMonthShort(1) + '–' + formatMonthShort(m);
    },

    // ------------------------------------------------------------ Maße je Gerät
    barsHeight() {
      return this.handy ? 24 : 32;
    },
    chart() {
      if (this.handy) {
        return { height: 150, gap: 2, axisWidth: 44, labelEvery: 14 };
      }
      return this.tablet
        ? { height: 200, gap: 3, axisWidth: 52, labelEvery: 7 }
        : { height: 220, gap: 4, axisWidth: 52, labelEvery: 7 };
    },
    donut() {
      if (this.handy) {
        return { size: 104, ring: 10, rowHeight: 44 };
      }
      return this.tablet ? { size: 120, ring: 12, rowHeight: 48 } : { size: 140, ring: 12, rowHeight: 44 };
    },
    mapHeight() {
      return this.handy ? 210 : (this.tablet ? 280 : 240);
    },

    // ------------------------------------------------------------ Quelle overview
    overview() {
      const s = this.src.overview;
      return s.s === 'ready' ? overviewValues(s.data) : { sum: NaN, count: NaN };
    },

    // ------------------------------------------------------------ Quelle saldo
    saldoRows() {
      const s = this.src.saldo;
      return s.s === 'ready' && s.year === this.ym.year ? s.data : null;
    },
    saldoPending() {
      return this.src.saldo.s === 'loading' || this.src.saldo.s === 'idle';
    },
    saldoFailed() {
      return this.src.saldo.s === 'error';
    },
    saldoForbidden() {
      return this.saldoFailed && this.src.saldo.status === 403;
    },
    // Einnahmen/Ticketverkäufe: ohne Link, solange im Fuß „Erneut versuchen“ steht (kein Knopf in einem Link)
    salesTileLink() {
      return this.saldoFailed && !this.saldoForbidden ? null : this.links.sales;
    },
    revenueAverages() {
      return this.saldoRows ? monthlyAverages(this.saldoRows, this.ym.year, this.ym.month, 'sale_amount', this.todayInfo) : [];
    },
    ticketAverages() {
      return this.saldoRows ? monthlyAverages(this.saldoRows, this.ym.year, this.ym.month, 'sale_count', this.todayInfo) : [];
    },
    revenueTrend() {
      return this.trendFoot(this.revenueAverages, formatEuro);
    },
    ticketTrend() {
      return this.trendFoot(this.ticketAverages, v => formatNumber(v));
    },
    saldo() {
      return this.saldoRows
        ? saldoSummary(this.saldoRows, this.ym.year, this.ym.month)
        : { total: NaN, bars: [], open: [], openSum: 0, balancedCount: 0 };
    },
    split() {
      return this.saldoRows ? paymentSplit(this.saldoRows, this.ym.year, this.ym.month) : null;
    },
    splitState() {
      return this.panelState('saldo');
    },
    // Namen aus der user-Liste; schlägt sie fehl, stehen „Fahrer {id}“ da
    open() {
      const user = this.src.user;
      const namesSettled = !this.needs.user || user.s === 'ready' || user.s === 'error';
      if (!this.saldoRows || !namesSettled) {
        return null;
      }
      return openAmounts(this.saldo, user.s === 'ready' ? user.data : [], 4);
    },
    // Fuß der Karte „Offene Beträge“: „{r} weitere offen“ mit Summe, sonst Haken und „{n} weitere Fahrer · ausgeglichen“
    openFoot() {
      const o = this.open;
      if (!o) {
        return null;
      }
      if (o.restCount > 0) {
        return { left: formatNumber(o.restCount) + ' weitere offen', right: formatEuro(o.restSum), icon: null };
      }
      if (o.balancedCount > 0) {
        const n = o.balancedCount;
        const left = o.rows.length ? formatNumber(n) + (n === 1 ? ' weiterer Fahrer' : ' weitere Fahrer') : formatNumber(n) + ' Fahrer';
        return { left, right: 'ausgeglichen', icon: 'check' };
      }
      return null;
    },
    openState() {
      if (!this.saldoRows) {
        return this.panelState('saldo');
      }
      return this.open ? null : 'loading';
    },

    // ------------------------------------------------------------ Quelle driversettlement
    revenueRange() {
      return this.src.driversettlement.range || revenueRange(this.ym.year, this.ym.month, this.todayInfo);
    },
    revenueDays() {
      const s = this.src.driversettlement;
      return s.s === 'ready' ? revenueDays(s.data, s.range.from, s.range.to) : null;
    },
    revenueState() {
      return this.panelState('driversettlement');
    },
    revenueSubtitle() {
      const range = this.revenueRange;
      let text = range.current ? (this.handy ? '30 Tage' : 'letzte 30 Tage') : formatMonth(range.from.slice(0, 7));
      if (this.revenueDays) {
        text += ' · Summe ' + formatEuro(this.revenueDays.reduce((acc, d) => acc + d.value, 0));
      }
      return text;
    },

    // ------------------------------------------------------------ Quellen gps, user, device
    gpsRows() {
      return this.src.gps.s === 'ready' ? this.src.gps.data : null;
    },
    buses() {
      return busSummary(this.gpsRows);
    },
    // Anteile nach Ampel wie auf der Karte: ≤ 5 Min., 5–10 Min., > 10 Min., ohne Angabe
    busParts() {
      const lv = this.buses.levels;
      return [
        { value: lv.ok, color: 'st-ok' },
        { value: lv.warn, color: 'st-warn' },
        { value: lv.late, color: 'st-late' },
        { value: lv.none, color: 'st-none' }
      ];
    },
    // Legende der Ampel (Kachel „Busse unterwegs“ und Live-Karte), überall im selben Minutenformat
    delayLegend() {
      return DELAY_LEGEND;
    },
    busCountText() {
      return this.buses.n === 1 ? '1 Bus' : formatNumber(this.buses.n) + ' Busse';
    },
    mapState() {
      if (this.gpsRows) {
        return this.buses.n ? null : 'empty';
      }
      return this.panelState('gps');
    },
    users() {
      return activeSummary(this.src.user.s === 'ready' ? this.src.user.data : null);
    },
    devices() {
      return activeSummary(this.src.device.s === 'ready' ? this.src.device.data : null);
    }
  },
  watch: {
    month(value, old) {
      this.onMonthChange(value, old);
    },
    // Rechte oder Gerät (Handy) geändert: fehlende Quellen nachladen
    needsKey() {
      this.ensureSources();
    }
  },
  created() {
    // nicht reaktiv: Zähler je Quelle (nur die letzte Antwort zählt) und Saldo-Cache je Jahr
    this.requestIds = {};
    SOURCES.forEach((key) => {
      this.requestIds[key] = 0;
    });
    this.saldoCache = {};
    this.saldoGeneration = 0;
    this.alive = true;
  },
  mounted() {
    setPhoneAction({ icon: 'refresh', label: 'Kennzahlen aktualisieren', handler: () => this.refreshAll() });
    this.ensureSources();
  },
  beforeDestroy() {
    this.alive = false;
    setPhoneAction(null);
  },
  methods: {
    // ------------------------------------------------------------ Laden
    baseUrl() {
      return this.$store.state.baseUrl;
    },
    // lädt eine Quelle; extra (z. B. Zeitraum) wird im Zustand mitgeführt
    fetch(key, url, extra) {
      const id = ++this.requestIds[key];
      this.src[key] = Object.assign(idle(), extra, { s: 'loading' });
      return axios.get(url).then((response) => {
        if (!this.alive || id !== this.requestIds[key]) {
          return;
        }
        this.src[key] = Object.assign(idle(), extra, { s: 'ready', status: response.status, data: freezeList(response.data) });
      }, (error) => {
        if (!this.alive || id !== this.requestIds[key]) {
          return;
        }
        console.error('Dashboard: ' + url + ' fehlgeschlagen', error);
        this.src[key] = Object.assign(idle(), extra, { s: 'error', status: statusOf(error) });
      });
    },
    reset(key) {
      this.requestIds[key] += 1;
      this.src[key] = idle();
    },
    // Monat und Jahr ohne führende Null wie bisher (Aufruf für den laufenden Monat bleibt byte-gleich)
    loadOverview() {
      const { year, month } = this.ym;
      this.fetch('overview', `${this.baseUrl()}ticketsale?sale_month=${month}&sale_year=${year}&option=overview`);
    },
    // Saldo je Jahr: im laufenden Jahr bis zum laufenden Monat (wie bisher), für Vorjahre bis 12; Cache je Jahr
    loadSaldo(force) {
      const year = this.ym.year;
      const cached = this.saldoCache[year];
      if (!force && cached) {
        this.requestIds.saldo += 1;
        this.src.saldo = Object.assign(idle(), { s: 'ready', status: 200, data: cached, year });
        return;
      }
      const id = ++this.requestIds.saldo;
      const generation = this.saldoGeneration;
      const toMonth = saldoToMonth(year, this.todayInfo);
      // je Monat eine eigene Anfrage, parallel (src/utils/saldoMonths.js; 13.10.2026: die Jahresabfrage brauchte bei
      // großen Mandanten über 20 s bzw. lief in das 29-s-Limit des API Gateway – Mandant 3 „Dashboard kann nicht
      // geladen werden“)
      this.src.saldo = Object.assign(idle(), { s: 'loading', year });
      loadSaldoMonths(this.baseUrl(), year, 1, year, toMonth).then((rows) => {
        const data = freezeList(rows);
        if (generation === this.saldoGeneration) {
          this.saldoCache[year] = data;
        }
        if (!this.alive || id !== this.requestIds.saldo) {
          return;
        }
        this.src.saldo = Object.assign(idle(), { s: 'ready', status: 200, data, year });
      }, (error) => {
        if (!this.alive || id !== this.requestIds.saldo) {
          return;
        }
        console.error('Dashboard: saldo ' + year + ' (Monate 1–' + toMonth + ') fehlgeschlagen', error);
        this.src.saldo = Object.assign(idle(), { s: 'error', status: statusOf(error), year });
      });
    },
    // laufender Monat: heute−29 bis heute; vergangener Monat: 1. bis Monatsende
    loadDriverSettlement() {
      const range = Object.freeze(revenueRange(this.ym.year, this.ym.month, this.todayInfo));
      this.fetch('driversettlement', `${this.baseUrl()}driversettlement?from_date=${range.from}&to_date=${range.to}`, { range });
    },
    loadLive(key) {
      const customerId = this.$store.state.customerId;
      const urls = {
        gps: `${this.baseUrl()}gps?customer_id=${customerId}&period=20`,
        user: `${this.baseUrl()}user?customer_id=${customerId}`,
        device: `${this.baseUrl()}device?customer_id=${customerId}`
      };
      this.fetch(key, urls[key]);
    },
    load(key) {
      if (key === 'overview') {
        this.loadOverview();
      } else if (key === 'saldo') {
        this.loadSaldo(false);
      } else if (key === 'driversettlement') {
        this.loadDriverSettlement();
      } else {
        this.loadLive(key);
      }
    },
    // benötigte Quellen laden, die noch nie geladen wurden
    ensureSources() {
      SOURCES.forEach((key) => {
        if (this.needs[key] && this.src[key].s === 'idle') {
          this.load(key);
        }
      });
    },
    // Monatswechsel: Einnahmen, Ticketverkäufe, Diagramm und Zahlungsarten folgen; Busse, Benutzer, Geräte und Karte
    // bleiben Live-Werte. Saldo nur bei Jahreswechsel (Cache je Jahr).
    onMonthChange(value, old) {
      const next = splitMonth(value);
      if (!next) {
        return;
      }
      const prev = splitMonth(old);
      ['overview', 'driversettlement'].forEach((key) => {
        if (this.needs[key]) {
          this.load(key);
        } else {
          this.reset(key);
        }
      });
      if (!prev || prev.year !== next.year) {
        if (this.needs.saldo) {
          this.loadSaldo(false);
        } else {
          this.reset('saldo');
        }
      }
    },
    // Aktualisieren: alle Quellen neu, Saldo-Cache leeren
    refreshAll() {
      const now = moment();
      this.today = now.format('YYYY-MM-DD');
      this.todayUtc = moment.utc().format('YYYY-MM-DD');
      this.saldoCache = {};
      this.saldoGeneration += 1;
      SOURCES.forEach((key) => {
        if (this.needs[key]) {
          if (key === 'saldo') {
            this.loadSaldo(true);
          } else {
            this.load(key);
          }
        } else {
          this.reset(key);
        }
      });
    },
    retry(key) {
      if (key === 'saldo') {
        this.loadSaldo(true);
      } else {
        this.load(key);
      }
    },

    // ------------------------------------------------------------ Zustände
    // AppKpiTile: 'loading' | 'ready' | 'error' | 'forbidden'
    tileState(key) {
      const s = this.src[key];
      if (s.s === 'ready') {
        return 'ready';
      }
      if (s.s === 'error') {
        return s.status === 403 ? 'forbidden' : 'error';
      }
      return 'loading';
    },
    // AppPanel/AppStateOverlay: null (fertig) | 'loading' | 'error' | 'forbidden'
    panelState(key) {
      const state = this.tileState(key);
      return state === 'ready' ? null : state;
    },
    // Handy: ganze Karte als Link, aber nicht im Fehlerzustand – der Knopf „Erneut versuchen“ läge sonst in einem
    // Link (ungültig, und ein Tipp darauf würde zugleich navigieren); dann führt der Link im Kopfstreifen weiter
    phoneCardLink(state) {
      return this.handy && state !== 'error';
    },

    // ------------------------------------------------------------ Anzeige
    // „Ø/Tag +4,2 % ggü. Sep.“ (Handy ohne Vergleichsmonat); ohne Vormonatswert „Ø/Tag {Wert}“ ohne Icon
    trendFoot(averages, format) {
      const m = this.ym.month;
      const trend = averageTrend(averages, m);
      if (trend !== null) {
        const pct = formatPercent(trend);
        return {
          icon: pct.charAt(0) === MINUS ? 'trendDown' : 'trendUp',
          text: 'Ø/Tag ' + pct + (this.handy ? '' : ' ggü. ' + formatMonthShort(m - 1))
        };
      }
      const avg = averages[m - 1];
      return { icon: null, text: 'Ø/Tag ' + (avg === null || avg === undefined ? LEER : format(avg)) };
    },
    activeParts(summary) {
      return [{ value: summary.active, color: 'd-foc' }];
    },
    isNumber(x) {
      return typeof x === 'number' && isFinite(x);
    },
    money(x) {
      return formatNumber(x, 2);
    },
    count(x) {
      return formatNumber(x);
    },
    euro(x) {
      return formatEuro(x);
    },
    plural(n, one, many) {
      return plural(n, one, many);
    },
    // Handy: ab 1 Mio. kleiner (17 px), damit der Wert nie abgeschnitten wird
    valueClass(x) {
      return this.handy && Math.abs(Number(x)) >= 1e6 ? 'ov__value--small' : null;
    },
    spacer(height) {
      return { height: height + 'px' };
    }
  }
};
</script>

<style scoped>
.ov {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ov--phone {
  gap: 12px;
}
.ov__stepper {
  display: flex;
}
/* 3 Spalten am Desktop, 2 im Tablet hoch, darunter 1 (höchstens 3 Spalten, auch bei breitem Blatt) */
.ov__kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(max(300px, calc((100% - 32px) / 3)), 100%), 1fr));
  gap: 16px;
}
.ov--phone .ov__kpis {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
/* Karten 2 : 1 genau im Raster der Kacheln darüber (Kundenwunsch 13.10.2026: „die einnahmenkarte … ist nicht ganz so
   breit wie die anderen zwei karten oben drüber … zahlungsarten ist breiter als aktive geräte“; vorher flex 2 : 1 nach
   Abzug nur eines Abstands, je rund 6 px versetzt): breit = zwei Spalten plus Abstand, schmal = eine Spalte von
   (100 % − 2 Abstände) / 3. Wie die Kacheln wird umbrochen, sobald eine Spalte unter 300 px fiele; dann je volle Breite. */
.ov__row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.ov__card--wide {
  flex: 1 0 calc((100% - 32px) * 2 / 3 + 16px);
  min-width: 0;
}
.ov__card--narrow {
  flex: 1 0 calc((100% - 32px) / 3);
  min-width: min(300px, 100%);
}
.ov--phone .ov__row {
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 12px;
}
.ov--phone .ov__card {
  flex: none;
  min-width: 0;
}
/* Kacheln und Handy-Karten sind Links: Text in Tintenfarbe statt Linkfarbe. Das Vuetify-Theme (.v-application a,
   zur Laufzeit nach allen Stilen eingefügt) gewinnt sonst gegen a.app-kpi / a.app-panel aus bedienelemente.css. */
.ov a.app-kpi,
.ov a.app-panel {
  color: var(--ink);
}
/* Live-Karte: füllt die innere Karte bis an den Rand (Körper ohne Polster; die Rundung schneidet der Körper) und
   wächst mit der Höhe der Nachbarkarte */
.ov__card--map ::v-deep .app-panel__body {
  display: flex;
  flex-direction: column;
}
.ov__card--map ::v-deep .ov-map__box {
  border-radius: 0;
}
/* Zahlungsarten: Inhalt füllt den gestreckten Kartenkörper und steht senkrecht mittig */
.ov__card--column ::v-deep .app-panel__body {
  display: flex;
  flex-direction: column;
}
p.ov__none {
  margin: 0;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
/* Verspätung in der Unterzeile der Live-Karte (Handy): Punkt in der Ampelfarbe, Text neutral. ::v-deep: der Text steht
   in einem Slot der Karte (ohne Scope-Attribut) */
.ov ::v-deep .ov__late {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  color: var(--ink-2);
}
.ov__value--small {
  font-size: 17px;
}
</style>
